import React, { RefObject } from 'react';
import { useTranslation } from 'react-i18next';
import { SVG, Icons } from '../../../../components/atoms/SVG';
import { Hyperlink } from '../../../../components/atoms/Hyperlink';
import { TooltipPlacement } from '../../../../components/atoms/Tooltip';
import { Namespace } from '../../../../translation/namespace';
import ILink from '../../../../types/ILink';
import { NavItem } from '../../../../types/api/NavItem';
import { TopItem } from './TopItem';
import { TopItemSymbol } from './TopItemSymbol';

type MainNavigationTopItemProps = {
  root: ILink;
  current: { level: number; navItem: NavItem };
  previous?: { link: ILink; title: string };
  ref?: RefObject<HTMLDivElement | null>
};

/**
 * This returns one of three variant of the MainNavigationTopItem:
 * Level 0 - The Homepage:
 * This returns a disabled link to the homepage only.
 *
 * Level 1 - Sub page on level 1:
 * It returns a disabled link to the current page and a homepage link.
 *
 * Level 2 and all other levels:
 * It returns a disabled link to the current page, a back link to the previous page and a homepage link.
 */

export function MainNavigationTopItem({ root, current, previous, ref }: MainNavigationTopItemProps) {
  const { t } = useTranslation(Namespace.MainNavigation);
  const { navItem, level } = current;

  // homepage
  if (level === 0) {
    return (
      <TopItem ref={ref}>
        {navItem.packState === 'onlyDraftVersion' && <TopItemSymbol type="draft" />}
        {navItem.packState === 'template' && <TopItemSymbol type="template" />}
        {/* this is a "disabled link", this is the proper markup for that */}
        {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
        <a
          className="main-navigation__link--top font-medium-15-19 main-navigation__link--active"
          role="link"
          aria-disabled="true"
          aria-current="page"
        >
          {t('topItem.homePage')}
        </a>
      </TopItem>
    );
  }

  if (level === 1) {
    // level 1
    return (
      <TopItem ref={ref}>
        <Hyperlink
          to={root.url}
          className="main-navigation__link font-medium-15-19 main-navigation__link--home-back"
          title={t('topItem.goToHomepage')}
          tooltipPlacement={TooltipPlacement.TOP_START}
        >
          <span className="invisible">{t('topItem.goToHomepage')}</span>
          <span className="main-navigation__iconcontainer main-navigation__icon-home">
            <SVG inverted svgClass="main-navigation__linksymbol" svgSpriteId={Icons.HOME_HOUSE} />
          </span>
        </Hyperlink>
        <span className="main-navigation__link--top font-medium-15-19 main-navigation__link--active">
          {navItem.packState === 'onlyDraftVersion' && <TopItemSymbol type="draft" />}
          {navItem.packState === 'template' && <TopItemSymbol type="template" />}
          {/* this is a "disabled link", this is the proper markup for that */}
          {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
          <a className="main-navigation__disabled-link" role="link" aria-disabled="true" aria-current="page">
            <span className="main-navigation__link-text">{navItem.link.name}</span>
          </a>
        </span>
      </TopItem>
    );
  }
  return (
    // all other levels
    <TopItem ref={ref}>
      <Hyperlink
        to={root.url}
        className="main-navigation__link font-medium-15-19 main-navigation__link--home-back main-navigation__link-home"
        title={t('topItem.goToHomepage')}
        tooltipPlacement={TooltipPlacement.TOP_START}
      >
        <span className="invisible">{t('topItem.goToHomepage')}</span>
        <span className="main-navigation__iconcontainer main-navigation__icon-home">
          <SVG inverted svgClass="main-navigation__linksymbol" svgSpriteId={Icons.HOME_HOUSE} />
        </span>
      </Hyperlink>
      {previous?.link.url && (
        <Hyperlink
          to={previous.link.url}
          className="main-navigation__link font-medium-15-19 main-navigation__link--home-back"
          // ReactDOMServer.renderToStaticMarkup escapes all characters,
          // so we need to replace escaped quotes with normal quotes
          title={t('topItem.goBack', { pageName: previous?.link.name })}
          hideTooltipFromScreenReader
          tooltipPlacement={TooltipPlacement.TOP_START}
        >
          <span className="invisible">{t('topItem.goBack', { pageName: previous?.link.name })}</span>
          <span className="main-navigation__iconcontainer">
            <SVG inverted svgClass="main-navigation__subsitearrow" svgSpriteId={Icons.ARROW_LEFT} />
          </span>
        </Hyperlink>
      )}
      <span className="main-navigation__link--top font-medium-15-19 main-navigation__link--active">
        {navItem.packState === 'onlyDraftVersion' && <TopItemSymbol type="draft" />}
        {navItem.packState === 'template' && <TopItemSymbol type="template" />}
        {/* this is a "disabled link", this is the proper markup for that */}
        {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
        <a className="main-navigation__disabled-link" role="link" aria-disabled="true" aria-current="page">
          <span className="main-navigation__link-text">{navItem.link.name}</span>
        </a>
      </span>
    </TopItem>
  );
}
